import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "preact/jsx-runtime";
import { useEffect, useMemo, useState } from 'preact/hooks';
import { useLocation } from 'preact-iso';
import { websApi } from '@/api/webs';
import { useToast } from '@/hooks/useToast';
import { Target, FileText } from 'lucide-preact';
import { getWebTypeMeta } from '@/constants/webTypes';
import { LeadGoalBar } from '@/components';
import { usePageHeader } from '@/hooks/usePageHeader';
import { routes } from '@/utils/routes';
import { WebDetailsTab } from './tabs/WebDetailsTab';
import { WebActivationsTab } from './tabs/WebActivationsTab';
import { MeetingsTab } from '@/components/common/MeetingsTab';
import { WebLeadsTab } from './tabs/WebLeadsTab';
import { WebSettingsTab } from './tabs/WebSettingsTab';
import { WebCalendarTab } from './tabs/WebCalendarTab';
import { formatEventDateRange } from '@/utils/dateTimeFormatters';
import { useTimeZones } from '@/components/calendar/hooks/useTimeZones';
export function WebDetail({ clientCompanyId, workspaceId, campaignId, webId }) {
    const location = useLocation();
    const { setHeader } = usePageHeader();
    const { getAbbreviation } = useTimeZones();
    // Data + ui state
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [web, setWeb] = useState(null);
    // (Top-level form moved into TopLevelEditSection component rendered in panels)
    // Load web
    useEffect(() => {
        let active = true;
        (async () => {
            setLoading(true);
            setError(null);
            const res = await websApi.getById(workspaceId, webId);
            if (!active)
                return;
            if (res.data)
                setWeb(res.data);
            else {
                const msg = res.error || 'Failed to load web details';
                setError(msg);
                showError('Load failed', msg);
            }
            setLoading(false);
        })();
        return () => { active = false; };
    }, [workspaceId, webId]);
    // (Audiences + form state now encapsulated inside TopLevelEditSection)
    // Breadcrumb header
    useEffect(() => {
        const r = routes.scoped(clientCompanyId, workspaceId);
        setHeader({ items: [
                { text: 'Campaigns', href: r.campaignsIndex() },
                { text: 'Campaign', href: r.campaign(campaignId) },
                { text: 'Web' }
            ] });
    }, [clientCompanyId, workspaceId, campaignId]);
    // (Live validation moved into TopLevelEditSection)
    // Type flags
    const isOnlineEvent = useMemo(() => (web?.Type || '').toLowerCase().includes('online') || !!web?.OnlineEvent, [web]);
    const isDownload = useMemo(() => (web?.Type || '').toLowerCase().includes('download') || !!web?.Download, [web]);
    const isMeeting = web?.Type === 'Meeting';
    const isDemo = web?.Type === 'Demo';
    // Loading states
    if (loading && !web)
        return (_jsxs("div", { className: "p-6 flex items-center gap-3 text-slate-600 dark:text-slate-300", children: [_jsx("div", { className: "w-5 h-5 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" }), "Loading web..."] }));
    if (error && !web)
        return _jsx("div", { className: "p-6 text-red-600 dark:text-red-400", children: error });
    if (!web)
        return null;
    // Derived visual values
    const statusTone = web.Status?.toLowerCase() === 'active'
        ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
        : 'bg-slate-200 text-slate-800 dark:bg-slate-700 dark:text-slate-200';
    const { showError } = useToast();
    // leads count used only in editor component now
    const typeMeta = getWebTypeMeta(web.Type);
    const TypeIcon = typeMeta.Icon;
    const typeChipClass = typeMeta.chipClass;
    // Actions
    // (Delete / archive / save handlers now inside TopLevelEditSection)
    const displayTitle = (() => {
        if (isOnlineEvent)
            return web.OnlineEvent?.Title || web.Name;
        if (isDownload)
            return web.Download?.Title || web.Name;
        if (isMeeting)
            return web.Meeting?.Title || web.Name;
        if (isDemo)
            return web.Demo?.Title || web.Name;
        return web.Name;
    })();
    const [activeTab, setActiveTab] = useState('details');
    // Sync tab with URL path to support deep linking (e.g., /web/:id/leads, /web/:id/leads/companies)
    useEffect(() => {
        const rawPath = location?.path || location?.url || (typeof window !== 'undefined' ? window.location.pathname : '');
        const leadsMatch = /\/web\/[^/]+\/leads(\/companies)?$/.test(rawPath);
        const activationsMatch = /\/web\/[^/]+\/activations$/.test(rawPath);
        const meetingsMatch = /\/web\/[^/]+\/meetings$/.test(rawPath);
        const calendarMatch = /\/web\/[^/]+\/calendar$/.test(rawPath);
        const settingsMatch = /\/web\/[^/]+\/settings$/.test(rawPath);
        const detailsMatch = /\/web\/[^/]+$/.test(rawPath);
        if (leadsMatch && activeTab !== 'leads')
            setActiveTab('leads');
        else if (activationsMatch && activeTab !== 'activations')
            setActiveTab('activations');
        else if (meetingsMatch && activeTab !== 'meetings')
            setActiveTab('meetings');
        else if (calendarMatch && activeTab !== 'calendar')
            setActiveTab('calendar');
        else if (settingsMatch && activeTab !== 'settings')
            setActiveTab('settings');
        else if (detailsMatch && activeTab !== 'details')
            setActiveTab('details');
    }, [location]);
    const scoped = routes.scoped(clientCompanyId, workspaceId);
    const routeToTab = (tab) => {
        switch (tab) {
            case 'details': {
                setActiveTab('details');
                const target = scoped.web(campaignId, webId);
                try {
                    location.route(target);
                }
                catch {
                    if (typeof window !== 'undefined')
                        window.history.pushState({}, '', target);
                }
                return;
            }
            case 'leads': {
                setActiveTab('leads');
                const target = `${scoped.web(campaignId, webId)}/leads`;
                try {
                    location.route(target);
                }
                catch {
                    if (typeof window !== 'undefined')
                        window.history.pushState({}, '', target);
                }
                return;
            }
            case 'activations': {
                setActiveTab('activations');
                const target = `${scoped.web(campaignId, webId)}/activations`;
                try {
                    location.route(target);
                }
                catch {
                    if (typeof window !== 'undefined')
                        window.history.pushState({}, '', target);
                }
                return;
            }
            case 'meetings': {
                setActiveTab('meetings');
                const target = `${scoped.web(campaignId, webId)}/meetings`;
                try {
                    location.route(target);
                }
                catch {
                    if (typeof window !== 'undefined')
                        window.history.pushState({}, '', target);
                }
                return;
            }
            case 'calendar': {
                setActiveTab('calendar');
                const target = `${scoped.web(campaignId, webId)}/calendar`;
                try {
                    location.route(target);
                }
                catch {
                    if (typeof window !== 'undefined')
                        window.history.pushState({}, '', target);
                }
                return;
            }
            case 'settings': {
                setActiveTab('settings');
                const target = `${scoped.web(campaignId, webId)}/settings`;
                try {
                    location.route(target);
                }
                catch {
                    if (typeof window !== 'undefined')
                        window.history.pushState({}, '', target);
                }
                return;
            }
        }
    };
    return (_jsxs("div", { className: "p-6 space-y-6", children: [_jsx("div", { className: "rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6 shadow-sm", children: _jsxs("div", { className: "flex flex-col lg:flex-row lg:items-start lg:justify-between gap-8", children: [_jsxs("div", { children: [_jsx("h1", { className: "text-2xl font-bold text-slate-900 dark:text-white tracking-tight break-words line-clamp-3 hover:line-clamp-none transition-all duration-200 cursor-pointer max-w-[58ch] md:max-w-[54ch]", title: displayTitle, onClick: e => {
                                        const el = e.currentTarget;
                                        if (el.classList.contains('line-clamp-3')) {
                                            el.classList.remove('line-clamp-3');
                                        }
                                        else {
                                            el.classList.add('line-clamp-3');
                                        }
                                    }, children: displayTitle }), isOnlineEvent && (_jsxs("div", { className: "mt-2", children: [_jsx("div", { className: "text-[11px] uppercase tracking-wide font-semibold text-slate-500 dark:text-slate-400", children: web.OnlineEvent?.ModeType?.toLowerCase() === 'on-demand (recording only)' ? 'Launch Date' : 'Broadcast Date' }), _jsx("div", { className: "text-sm font-medium text-slate-800 dark:text-slate-200", children: web.OnlineEvent?.ModeType?.toLowerCase() === 'on-demand (recording only)'
                                                ? formatEventDateRange(web.LaunchDate, undefined, undefined)
                                                : formatEventDateRange(web.OnlineEvent?.StartDateTime, web.OnlineEvent?.EndDateTime, web.OnlineEvent?.TimeZone ? getAbbreviation(web.OnlineEvent.TimeZone) : undefined) })] })), _jsxs("div", { className: `${isOnlineEvent ? 'mt-4' : 'mt-3'} flex flex-wrap items-center gap-2`, children: [_jsxs("span", { className: `inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full ${typeChipClass}`, children: [_jsx(TypeIcon, { className: "w-4 h-4" }), web.Type] }), _jsxs("span", { className: `inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full ${statusTone}`, children: [_jsx("span", { className: "w-2 h-2 rounded-full bg-current opacity-70" }), web.Status] })] }), web.IncludedFileImports && web.IncludedFileImports.length > 0 && (_jsxs("div", { className: "mt-3 flex flex-wrap items-center gap-2", children: [_jsx("span", { className: "text-[11px] uppercase tracking-wide font-semibold text-slate-500 dark:text-slate-400 mr-1", children: "Audiences:" }), web.IncludedFileImports.map(fi => (_jsxs("span", { className: "inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded-full bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300", children: [_jsx(FileText, { className: "w-3 h-3" }), fi.Name] }, fi.Id)))] }))] }), _jsx("div", { className: "flex items-center gap-8", children: _jsxs("button", { type: "button", onClick: () => routeToTab('leads'), "aria-label": "Leads progress", className: "group relative text-center px-5 py-4 rounded-xl border border-slate-200 dark:border-slate-600 bg-white/60 dark:bg-slate-900/30 backdrop-blur-sm hover:border-primary-400 dark:hover:border-primary-500 hover:shadow-md transition-all", children: [_jsx("div", { className: "absolute inset-0 rounded-xl pointer-events-none opacity-0 group-hover:opacity-100 bg-gradient-to-br from-primary-50/70 to-primary-100/10 dark:from-primary-500/10 dark:to-primary-400/0 transition" }), _jsxs("div", { className: "relative flex flex-col items-center", children: [_jsx("div", { className: "w-full flex items-center justify-center gap-2 text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1", children: _jsx("span", { children: "Leads Progress" }) }), (() => {
                                                const goal = Math.max(0, web.LeadGoal || 0);
                                                const sourced = Math.max(0, web.LeadCount || 0);
                                                const pctRaw = goal > 0 ? Math.round((sourced / goal) * 100) : null;
                                                return (_jsxs(_Fragment, { children: [_jsxs("div", { className: "text-2xl font-semibold text-slate-900 dark:text-white flex items-center gap-2", children: [_jsxs("span", { className: "relative inline-flex", children: [_jsx(Target, { className: "w-6 h-6 text-primary-600 dark:text-primary-500 transform transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3" }), _jsx("span", { className: "absolute -inset-1 rounded-full opacity-0 group-hover:opacity-70 bg-primary-500/10 blur-sm transition" })] }), _jsxs("span", { children: [sourced.toLocaleString(), " ", goal > 0 && _jsxs("span", { className: "text-slate-400 dark:text-slate-500", children: ["/ ", goal.toLocaleString()] })] })] }), _jsx("div", { className: "mt-2", children: _jsx(LeadGoalBar, { sourced: sourced, goal: goal, mode: "simple", trackClassName: "w-44 h-2 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden", fillClassName: "bg-primary-600", animate: true, animationMs: 500 }) }), _jsx("div", { className: "mt-1 text-[11px] font-medium text-slate-600 dark:text-slate-400 tracking-wide", children: pctRaw !== null ? `${pctRaw}%` : '—' })] }));
                                            })()] })] }) })] }) }), _jsx("div", { className: "flex flex-wrap items-center gap-1 border-b border-slate-200 dark:border-slate-700", children: [
                    { key: 'details', label: 'Details' },
                    { key: 'activations', label: 'Activations' },
                    { key: 'meetings', label: 'Meetings' },
                    { key: 'calendar', label: 'Calendar' },
                    { key: 'leads', label: 'Leads' },
                    { key: 'settings', label: 'Settings' }
                ].map(t => (_jsxs("button", { type: "button", onClick: () => routeToTab(t.key), className: `px-4 py-2 text-sm font-medium rounded-t-md transition-colors relative
                            ${activeTab === t.key ? 'text-primary-700 dark:text-primary-400' : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white'}
                        `, children: [t.label, _jsx("span", { className: `absolute left-0 right-0 -bottom-px h-0.5 rounded-full transition-all ${activeTab === t.key ? 'bg-primary-600 dark:bg-primary-500' : 'bg-transparent'}` })] }, t.key))) }), _jsxs("div", { children: [activeTab === 'details' && (_jsx(WebDetailsTab, { web: web, onPatchWeb: (patcher) => setWeb(prev => (prev ? patcher(prev) : prev)) })), activeTab === 'meetings' && (_jsx(MeetingsTab, { web: web, workspaceId: workspaceId, campaignId: campaignId })), activeTab === 'calendar' && (_jsx(WebCalendarTab, { web: web, clientCompanyId: clientCompanyId })), activeTab === 'activations' && (_jsx(WebActivationsTab, { web: web, workspaceId: workspaceId, campaignId: campaignId })), activeTab === 'leads' && (_jsx(WebLeadsTab, { web: web, clientCompanyId: clientCompanyId, workspaceId: workspaceId, campaignId: campaignId })), activeTab === 'settings' && (_jsx(WebSettingsTab, { web: web, onPatchWeb: (patcher) => setWeb(prev => (prev ? patcher(prev) : prev)), clientCompanyId: clientCompanyId, workspaceId: workspaceId, campaignId: campaignId }))] })] }));
}
